import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Flex,
  Popover,
  PopoverTrigger,
  SimpleGrid,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import CheckTable from 'views/admin/dataTables/components/CheckTable';
import { columnsDataCheck } from 'views/admin/dataTables/variables/columnsData';
import MiniStatistics from 'components/card/MiniStatistics';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import CustomizeTablePopup from 'components/peoplePage/CustomisedTablePopUp';
import CustomSelect from 'components/peoplePage/CustomSelect';
import PersonalInfo from 'components/personalInfo/PersonalInfo';
import ShareInviteModal from './InviteModal/InviteModal';
import { DeleteIcon, AddIcon } from '@chakra-ui/icons';
import DeleteUserModel from './DeleteUser/DeleteUser';
import BulkAssignmentModal from './components/BulkAssignmentModal';
import InviteNewPeople from 'assets/icons/InviteNewPeople';
import EyeIcon from 'assets/icons/eyeIcon';
import analytics from '../../../segmentTracking';
import { handleSessionTimeout } from 'utils/sessionManagementUtils';
import api from '../../../utils/api';
import MultiSelectDropdown from './components/MultiSelectDropdown';
import FilterConfigPanel from './components/FilterConfigPanel';

export default function Settings() {
  const [currentUser] = useState(JSON.parse(localStorage.getItem('user')));
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedTab, setSelectedTab] = useState('All Users');
  const [isLoading, setIsLoading] = useState(false);
  const {
    isOpen: isOpenDelete,
    onOpen: onOpenDelete,
    onClose: onCloseDelete,
  } = useDisclosure();
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const getInitialVisibleColumns = useCallback(() => {
    const userRole = currentUser?.role?.toLowerCase();

    if (userRole === 'super_admin') {
      return {
        name: true, // Full Name
        username: true, // Username (new column)
        email: true, // Email
        status: true, // Status
        id: true, // Twella ID
        ipLocation: true, // IP Location
        subscriber: true, // Subscription
        lastActivityTime: true, // Last Activity
        // Hidden by default
        location: false, // GPS Location - never show
        lastSessions: false,
        totalSessions: false,
        age: false,
        gender: false,
        race: false,
        politicalPreferences: false,
        schools: false, // Schools - hidden by default
        courses: false, // Courses - hidden by default
        classes: false, // Classes - hidden by default
        instructors: false, // Instructors - hidden by default
      };
    } else {
      return {
        name: true, // Full Name
        username: true, // Username (new column)
        email: true, // Email
        status: true, // Status
        lastActivityTime: true, // Last Activity
        // Hide all others
        id: false,
        location: false, // GPS Location - never show
        ipLocation: false,
        lastSessions: false,
        totalSessions: false,
        subscriber: false,
        age: false,
        gender: false,
        race: false,
        politicalPreferences: false,
        schools: false, // Schools - hidden by default
        courses: false, // Courses - hidden by default
        classes: false, // Classes - hidden by default
        instructors: false, // Always hidden for instructors, hidden by default for others
      };
    }
  }, [currentUser?.role]);

  const [visibleColumns, setVisibleColumns] = React.useState(
    getInitialVisibleColumns(),
  );
  const [totalUsersLength, setTotalUsersLength] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isInviteUsers, setIsInviteUsers] = useState(false);
  const [isSuperRole, setIsSuperRole] = useState(false);
  const toast = useToast();
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [statisticsData, setStatisticsData] = useState({});
  const [showProfile, setShowProfile] = useState(false);
  const [singleUserData, setSingleUserData] = useState({});
  const [periods, setPeriods] = useState({
    peoplesPage: 'all',
    postNumber: 'all',
    activityTime: 'all',
    timeSpendOnCheckIn: 'all',
    awarenessDistribution: 'all',
    timeSpendOnCloserLook: 'all',
    evaluationSkills: 'all',
    thinking: 'all',
    categories: 'all',
  });
  const [userStatus, setUserStatus] = useState('All Users');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false); // eslint-disable-line
  const [currentInstitution] = useState(
    JSON.parse(localStorage.getItem('institution')),
  );

  // Filter System State Management
  const [filterOptions, setFilterOptions] = useState({
    schools: [],
    courses: [],
    instructors: [],
  });

  const [selectedFilters, setSelectedFilters] = useState({
    schools: [],
    courses: [],
    classes: [],
    instructors: [],
  });

  const [visibleFilters, setVisibleFilters] = useState({
    schools: false,
    courses: false,
    instructors: false,
  });

  // const { trackEvent } = useSegmentTracking();
  const periodOptions = [
    { value: 'all', display: 'All' },
    { value: 'week', display: 'Last Week' },
    { value: 'month', display: 'Last Month' },
    { value: '3months', display: 'Last 3 Months' },
    { value: '6months', display: 'Last 6 Months' },
    { value: 'year', display: 'Last Year' },
  ];

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));

    if (user?.role.toLowerCase() === 'super_admin') {
      setIsSuperRole(true);
    } else {
      setIsSuperRole(false);
    }

    // Update visible columns when user role changes
    setVisibleColumns(getInitialVisibleColumns());
  }, [currentUser?.role, getInitialVisibleColumns]);

  // Debounce search term - only trigger search after 500ms of no typing
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only set debounced search if term is >= 2 characters or empty
      if (searchTerm.length >= 2 || searchTerm.length === 0) {
        setDebouncedSearchTerm(searchTerm);
        setCurrentPage(1); // Reset to first page when searching
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Search status for tooltip feedback
  const searchStatus = useMemo(() => {
    if (searchTerm === '') {
      return { message: '', showTooltip: false };
    } else if (searchTerm.length < 2) {
      return {
        message: 'Type at least 2 characters to search...',
        showTooltip: true,
      };
    } else {
      return { message: '', showTooltip: false };
    }
  }, [searchTerm]);

  const getDisplayValue = () => {
    const selectedOption = periodOptions.find(
      (option) => option.value === periods.peoplesPage,
    );
    return selectedOption && selectedOption.display;
  };

  const statistics = [
    {
      name: 'Total users',
      value: statisticsData.totalUsers,
      change: '',
      text: '',
    },
    {
      name: 'New users',
      value: statisticsData?.newUsers?.count,
      syntax: statisticsData?.newUsers?.growth || '',
      text: statisticsData?.newUsers?.percentage
        ? `${statisticsData?.newUsers?.percentage?.toFixed(0)}% ${
            statisticsData?.newUsers?.growth === 'more'
              ? 'increase'
              : 'decrease'
          } in ${getDisplayValue()}`
        : '',
      syntaxFill: statisticsData?.newUsers?.growth
        ? `${
            statisticsData?.newUsers?.growth === 'more' ? '#25A259' : '#E02B00'
          }`
        : '',
    },
    {
      name: 'Total sessions',
      value: statisticsData?.totalSessions?.count
        ? statisticsData?.totalSessions?.count
        : '',
      syntax: statisticsData?.totalSessions?.growth
        ? `${statisticsData?.totalSessions?.growth}`
        : '',
      text: statisticsData?.totalSessions?.percentage
        ? `${
            statisticsData?.totalSessions?.percentage
              ? statisticsData?.totalSessions?.percentage?.toFixed(0)
              : 0
          }% ${
            statisticsData?.totalSessions?.growth === 'more'
              ? 'increase'
              : 'decrease'
          } in ${getDisplayValue()}`
        : '',
      syntaxFill: statisticsData?.totalSessions?.growth
        ? `${
            statisticsData?.totalSessions?.growth === 'more'
              ? '#25A259'
              : '#E02B00'
          }`
        : '',
    },
  ];

  const handleSendInvite = (data) => {
    setIsModalOpen(false);
  };

  // Build filter parameters for API
  const buildFilterParams = useCallback(() => {
    const params = [];

    if (selectedFilters.schools.length > 0) {
      params.push(`school=${selectedFilters.schools.join(',')}`);
    }

    if (selectedFilters.courses.length > 0) {
      params.push(`course=${selectedFilters.courses.join(',')}`);
    }

    if (selectedFilters.classes.length > 0) {
      params.push(`class=${selectedFilters.classes.join(',')}`);
    }

    if (selectedFilters.instructors.length > 0) {
      params.push(`instructor=${selectedFilters.instructors.join(',')}`);
    }

    // Add search parameter if search term exists and is >= 2 characters
    if (debouncedSearchTerm && debouncedSearchTerm.length >= 2) {
      params.push(`search=${encodeURIComponent(debouncedSearchTerm)}`);
    }

    return params.length > 0 ? `&${params.join('&')}` : '';
  }, [selectedFilters, debouncedSearchTerm]);

  const handleDeleteUser = async (data) => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication token is missing.');
    }

    const emails = data.map((user) => user.email).filter(Boolean);
    const phones = data.map((user) => user.phone).filter(Boolean);
    const body = { emails, phones };
    try {
      let DELETE_URL;
      if (isInviteUsers) {
        DELETE_URL = `/dashboard/users/deleteInvitedUser`;
      } else {
        DELETE_URL = `/user/profile/deleteUserByAdmin`;
      }

      const { data: responseData } = await api.post(DELETE_URL, body);

      if (responseData?.message) {
        toast({
          title: 'Users Deleted',
          description: responseData?.message,
          variant: 'solid',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        if (isInviteUsers) {
          await fetchAndFilterUsers();
        } else {
          await fetchAndFilterUsers(
            '/dashboard/users',
            periods.peoplesPage,
            'users',
          );
        }
      } else {
        throw new Error(responseData?.message || 'Failed to delete users.');
      }
    } catch (error) {
      console.error('Error deleting users:', error);
      toast({
        title: 'Error',
        description:
          error.response?.data?.message ||
          error.message ||
          'An error occurred while deleting users.',
        variant: 'solid',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }

    setSelectedRows([]);
    onCloseDelete();
  };

  const handleBulkAssignment = async (assignmentPayload) => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication token is missing.');
    }
    const body = {
      userIds: assignmentPayload.userIds,
      schools: assignmentPayload.schools,
      courses: assignmentPayload.courses,
      classes: assignmentPayload.classes,
      instructors: assignmentPayload.instructors,
    };

    try {
      const { data: responseData } = await api.patch(
        '/dashboard/users/bulk-assign-multiple',
        body,
      );

      if (responseData?.message) {
        toast({
          title: 'Users Assigned Successfully',
          description:
            'Users have been successfully assigned to the selected academic programs',
          variant: 'solid',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Refresh the user list
        await fetchAndFilterUsers(
          '/dashboard/users',
          periods.peoplesPage,
          'users',
        );
      } else {
        throw new Error(responseData?.message || 'Failed to assign users.');
      }
    } catch (error) {
      console.error('Error assigning users:', error);
      toast({
        title: 'Error',
        description:
          error.response?.data?.message ||
          error.message ||
          'An error occurred while assigning users.',
        variant: 'solid',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }

    setSelectedRows([]);
    setIsAssignmentModalOpen(false);
  };

  const fetchAndFilterUsers = useCallback(
    async (endpoint, period, dataKey) => {
      setIsLoading(true);
      try {
        let url = `/dashboard/users?skip=${
          (currentPage - 1) * itemsPerPage
        }&limit=${itemsPerPage}`;
        console.log(url);
        // Add period parameter only if it's not 'all'
        if (period !== 'all') {
          url += `&period=${period}`;
        }

        // Add institution parameter
        url += `&institution=${currentInstitution?._id}`;

        // Add user status and filter logic
        if (isInviteUsers || userStatus === 'inviteUsers') {
          url = `/dashboard/users/getInvitedAppUsers?skip=${
            (currentPage - 1) * itemsPerPage
          }&limit=${itemsPerPage}&institution=${currentInstitution?._id}`;
        } else if (userStatus !== 'All Users') {
          url += `&subscriber=${userStatus}`;
        }

        // Add instructor email parameter for instructor users (for both regular and invited users)
        if (
          !isInviteUsers &&
          currentUser?.role?.toLowerCase() === 'instructor' &&
          currentUser?.email
        ) {
          url += `&instructor=${currentUser.email}`;
        }

        // Add filter parameters only for non-invited users
        if (!isInviteUsers && userStatus !== 'inviteUsers') {
          url += buildFilterParams();
        }

        console.log('2----', url);

        const response = await api.get(url);

        const usersArray = Array.isArray(
          response.data.data.users || response.data.data,
        )
          ? response.data.data.users || response.data.data
          : Object.values(response.data.data.users || response.data.data);

        setTotalUsersLength(
          response?.data?.data?.filteredUsersCount || response?.data?.total,
        );
        setFilteredUsers(usersArray);
        userStatus !== 'inviteUsers' && setStatisticsData(response.data.data);
      } catch (error) {
        console.error('Error fetching users:', error);
        setFilteredUsers([]);
        handleSessionTimeout(error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      currentInstitution,
      currentPage,
      itemsPerPage,
      userStatus,
      isInviteUsers,
      currentUser?.email,
      currentUser?.role,
      buildFilterParams,
    ],
  );

  useEffect(() => {
    fetchAndFilterUsers('/dashboard/users', periods.peoplesPage, 'users');
  }, [
    fetchAndFilterUsers,
    periods.peoplesPage,
    currentPage,
    itemsPerPage,
    debouncedSearchTerm,
  ]);

  const handleUserStatusChange = (newStatus) => {
    setUserStatus(newStatus);
    setIsInviteUsers(newStatus === 'inviteUsers');
    setCurrentPage(1);
  };

  const mapUserData = useCallback((users) => {
    if (!Array.isArray(users) || users.length === 0) {
      return [{ message: 'No records found' }];
    }

    return users.map((user) => ({
      name: user.fullName || user.name || '', // Full Name for display
      username: user.name || '', // Username (original name field)
      email: user.email || '',
      phoneNumber: user.phone || '',
      id: user.uid || '', // Twella ID
      clerkUserId: user.clerkUserId || '',
      userId: user._id,
      // GPS Location - never expose this field
      // location: user.location ? `${user.location.city}, ${user.location.state}` : '',
      ipLocation:
        user?.ipState && user?.ipCountry
          ? `${user.ipState}, ${user.ipCountry}`
          : '',
      lastSession: user?.lastSession
        ? new Date(user.lastSession).toLocaleDateString('en-US', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
          })
        : '',
      totalSessions: user?.totalSessions || 0,
      status: user.verificationStatus || '',
      subscriber: user.subscriber || '',
      lastActivityTime: user?.lastActivityTime
        ? new Date(user.lastActivityTime).toLocaleString('en-US', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          })
        : '',
      age: user.birthDate ? calculateAge(user.birthDate) : '',
      gender: user.gender ? user.gender.name : '',
      race: user.racialIdentity ? user.racialIdentity.name : '',
      state: user?.state || user?.ipState || user?.location?.state,
      country: user?.country || user?.ipCountry || user?.location?.country,
      politicalPreferences: user.politicalPreference
        ? user.politicalPreference.name
        : '',
      institution: user?.institution || '',
      // New academic columns
      schools:
        user.schools && Array.isArray(user.schools)
          ? user.schools.map((school) => school.name || school).join(', ')
          : '',
      courses:
        user.courses && Array.isArray(user.courses)
          ? user.courses.map((course) => course.name || course).join(', ')
          : '',
      classes:
        user.classes && Array.isArray(user.classes)
          ? user.classes.map((cls) => cls.name || cls).join(', ')
          : '',
      instructors:
        user.instructors && Array.isArray(user.instructors)
          ? user.instructors
              .map(
                (instructor) =>
                  instructor.fullName || instructor.name || instructor,
              )
              .join(', ')
          : '',
    }));
  }, []);

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birthDateObj = new Date(birthDate);
    let age = today.getFullYear() - birthDateObj.getFullYear();
    const monthDiff = today.getMonth() - birthDateObj.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDateObj.getDate())
    ) {
      age--;
    }
    return age.toString();
  };

  const handlePeriodChange = (dataKey, newPeriod) => {
    if (!isInviteUsers) {
      setPeriods((prevPeriods) => ({
        ...prevPeriods,
        [dataKey]: newPeriod,
      }));
    }
  };

  const handleUsersTab = (tabType) => {
    handleUserStatusChange(tabType);
    setSelectedTab(tabType);
    // Track users tab change
    analytics.track('people:userstab:clicked', {
      email: currentUser?.email,
      userRole: currentUser?.role,
      tab: tabType,
    });
  };

  // Filter API Functions
  const fetchFilterOptions = useCallback(async () => {
    try {
      let response;

      if (currentUser?.role?.toLowerCase() === 'instructor') {
        // Fetch instructor-specific filters
        response = await api.get(
          '/dashboard/users/getUserPageInstructorFilters',
        );
      } else {
        // Fetch admin/super admin filters
        response = await api.get(
          `/dashboard/users/getUserPageAdminFilters?institution=${currentInstitution?._id}`,
        );
      }

      if (response?.data?.success && response?.data?.data) {
        setFilterOptions({
          schools: response.data.data.schools || [],
          courses: response.data.data.courses || [],
          instructors: response.data.data.instructors || [],
        });
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
      toast({
        title: 'Error',
        description: 'Failed to load filter options',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      handleSessionTimeout(error);
    }
  }, [currentUser?.role, currentInstitution?._id, toast]);

  // Load filter options on component mount
  useEffect(() => {
    if (currentUser && currentInstitution) {
      fetchFilterOptions();
    }
  }, [fetchFilterOptions, currentUser, currentInstitution]);

  // Filter Selection Handlers
  const handleFilterChange = (filterType, selectedValues) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterType]: selectedValues,
    }));

    // Reset classes when courses change
    if (filterType === 'courses') {
      setSelectedFilters((prev) => ({
        ...prev,
        classes: [],
      }));
    }

    // Reset to first page when filters change
    setCurrentPage(1);
  };

  const getFilterOptions = (filterType) => {
    const options = filterOptions[filterType] || [];

    switch (filterType) {
      case 'schools':
        return options.map((school) => ({
          value: school._id,
          label: school.name,
        }));

      case 'courses':
        return options.map((course) => ({
          value: course._id,
          label: course.name,
        }));

      case 'instructors':
        return options.map((instructor) => ({
          value: instructor.email,
          label: instructor.fullName,
        }));

      default:
        return [];
    }
  };

  const getClassesForSelectedCourses = () => {
    const selectedCourseIds = selectedFilters.courses;
    const allClasses = [];

    selectedCourseIds.forEach((courseId) => {
      const course = filterOptions.courses.find((c) => c._id === courseId);
      if (course && course.classes) {
        course.classes.forEach((cls) => {
          allClasses.push({
            value: cls._id,
            label: `${course.name} - ${cls.name}`,
            courseId: courseId,
            courseName: course.name,
            className: cls.name,
          });
        });
      }
    });

    return allClasses;
  };

  // Check if classes dropdown should be visible
  const shouldShowClassesFilter = () => {
    return (
      visibleFilters.courses &&
      selectedFilters.courses.length > 0 &&
      getClassesForSelectedCourses().length > 0
    );
  };

  // Clear classes when they become invalid (courses deselected)
  useEffect(() => {
    if (selectedFilters.classes.length > 0) {
      const availableClasses = getClassesForSelectedCourses();
      const availableClassIds = availableClasses.map((cls) => cls.value);

      // Filter out invalid class selections
      const validClasses = selectedFilters.classes.filter((classId) =>
        availableClassIds.includes(classId),
      );

      // Update if there are invalid selections
      if (validClasses.length !== selectedFilters.classes.length) {
        setSelectedFilters((prev) => ({
          ...prev,
          classes: validClasses,
        }));
      }
    }
  }, [selectedFilters.courses, selectedFilters.classes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Render filter dropdowns component
  const renderFilterDropdowns = () => {
    const filters = [];

    // Schools filter
    if (visibleFilters.schools) {
      filters.push(
        <MultiSelectDropdown
          key="schools"
          options={getFilterOptions('schools')}
          selectedValues={selectedFilters.schools}
          onChange={(values) => handleFilterChange('schools', values)}
          placeholder="Select schools"
          width="180px"
        />,
      );
    }

    // Courses filter
    if (visibleFilters.courses) {
      filters.push(
        <MultiSelectDropdown
          key="courses"
          options={getFilterOptions('courses')}
          selectedValues={selectedFilters.courses}
          onChange={(values) => handleFilterChange('courses', values)}
          placeholder="Select courses"
          width="180px"
        />,
      );
    }

    // Classes filter (only show if courses are selected)
    if (shouldShowClassesFilter()) {
      filters.push(
        <MultiSelectDropdown
          key="classes"
          options={getClassesForSelectedCourses()}
          selectedValues={selectedFilters.classes}
          onChange={(values) => handleFilterChange('classes', values)}
          placeholder="Select classes"
          width="220px"
        />,
      );
    }

    // Instructors filter (only for non-instructor roles)
    if (
      visibleFilters.instructors &&
      currentUser?.role?.toLowerCase() !== 'instructor'
    ) {
      filters.push(
        <MultiSelectDropdown
          key="instructors"
          options={getFilterOptions('instructors')}
          selectedValues={selectedFilters.instructors}
          onChange={(values) => handleFilterChange('instructors', values)}
          placeholder="Select instructors"
          width="180px"
        />,
      );
    }

    return filters;
  };

  // Check if any filters are visible
  const hasVisibleFilters = () => {
    if (currentUser?.role?.toLowerCase() === 'instructor') {
      return visibleFilters.schools || visibleFilters.courses;
    }
    return (
      visibleFilters.schools ||
      visibleFilters.courses ||
      visibleFilters.instructors
    );
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return (
      selectedFilters.schools.length > 0 ||
      selectedFilters.courses.length > 0 ||
      selectedFilters.classes.length > 0 ||
      selectedFilters.instructors.length > 0
    );
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedFilters({
      schools: [],
      courses: [],
      classes: [],
      instructors: [],
    });
    setCurrentPage(1);

    // Track clear filters action
    analytics.track('people:filters:cleared', {
      email: currentUser?.email,
      userRole: currentUser?.role,
    });
  };

  return (
    <Box
      pt={{ base: '130px', md: '80px', xl: '35px' }}
      style={{ marginTop: '5px' }}
      position={'relative'}
    >
      {showProfile === false ? (
        <>
          <Box
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'space-between'}
          >
            <Text
              fontSize={'28px'}
              fontWeight={'bold'}
              color={'#222222'}
              lineHeight={'32px'}
              // onClick={() => trackEvent('People Page Clicked')}
            >
              People
            </Text>
            <Box
              width={'161px'}
              height={'32px'}
              borderRadius={'8px'}
              bg={'#EF6506'}
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              padding={'6px 8px 6px 8px'}
              gap={'8px'}
              cursor={'pointer'}
              onClick={() => {
                onOpen();
                // Track invite new people (clicked)
                analytics.track('people:invitenewpeople:clicked', {
                  email: currentUser.email,
                  userRole: currentUser?.role,
                });
              }}
              justifyContent={'center'}
            >
              {/* <ShareAndroidIcon /> */}
              <InviteNewPeople fill={'#FFFDFB'} />
              <Text fontWeight={'700'} fontSize={'14px'} color="white">
                Invite new people
              </Text>
            </Box>
          </Box>
          <ShareInviteModal
            isOpen={isOpen}
            onClose={onClose}
            onSendInvite={handleSendInvite}
          />
          <BulkAssignmentModal
            isOpen={isAssignmentModalOpen}
            onClose={() => setIsAssignmentModalOpen(false)}
            selectedUsers={selectedRows}
            onAssignUsers={handleBulkAssignment}
            filterOptions={filterOptions}
            currentUser={currentUser}
          />
          <SimpleGrid mt={'4'} spacing={{ base: '10px', xl: '10px' }}>
            <SimpleGrid
              width={'100%'}
              columns={{ base: 1, md: 2, lg: 3, '2xl': 6 }}
              gap="8px"
              bg={'#FFFDFB'}
              justifyContent={'space-between'}
              className="miniStatisticsContainer"
            >
              {statistics?.map((stat, index) => (
                <MiniStatistics
                  key={index}
                  name={stat?.name}
                  syntax={stat?.syntax}
                  syntaxFill={stat?.syntaxFill}
                  text={stat?.text}
                  value={stat?.value?.count || stat?.value || 0}
                  change={stat?.change}
                />
              ))}
            </SimpleGrid>

            {/* Filter Dropdowns Section - Between Cards and Tabs */}
            {hasVisibleFilters() && (
              <Flex
                alignItems="flex-end"
                justifyContent="flex-end"
                gap="15px"
                mt="8px"
                mb="8px"
                flexWrap="wrap"
                // px="20px"
              >
                {renderFilterDropdowns()}
                {hasActiveFilters() && (
                  <Button
                    size="sm"
                    variant="outline"
                    borderColor="#E7E7E7"
                    color="#EF6506"
                    _hover={{ bg: '#FFF8F3', borderColor: '#EF6506' }}
                    onClick={clearAllFilters}
                    height="32px"
                    fontSize="12px"
                    mt="auto"
                  >
                    Clear Filters
                  </Button>
                )}
              </Flex>
            )}

            <Flex
              justifyContent="space-between"
              alignItems="center"
              mt={hasVisibleFilters() ? '0' : '12px'}
            >
              <Flex alignItems="center" gap="10px">
                {/* <CustomSelect
                  options={usersOptions}
                  width="150px"
                  value={userStatus}
                  onChange={(newStatus) => handleUserStatusChange(newStatus)}
                  placeholder={'All Users'}
                /> */}
                <Flex
                  gap={'24px'}
                  marginTop={4}
                  marginLeft={5}
                  fontWeight={600}
                  marginBottom={0.1}
                >
                  <Text
                    cursor="pointer"
                    onClick={() => handleUsersTab('All Users')}
                    borderBottom={
                      selectedTab === 'All Users'
                        ? '2.5px solid #EF6506'
                        : 'none'
                    }
                    color={selectedTab === 'All Users' ? '#EF6506' : 'none'}
                    fontSize={'14px'}
                  >
                    All Users
                  </Text>
                  <Text
                    cursor="pointer"
                    onClick={() => handleUsersTab('Active')}
                    borderBottom={
                      selectedTab === 'Active' ? '2.5px solid #EF6506' : 'none'
                    }
                    color={selectedTab === 'Active' ? '#EF6506' : 'none'}
                    fontSize={'14px'}
                  >
                    Active
                  </Text>
                  <Text
                    cursor="pointer"
                    onClick={() => handleUsersTab('Trial')}
                    borderBottom={
                      selectedTab === 'Trial' ? '2.5px solid #EF6506' : 'none'
                    }
                    color={selectedTab === 'Trial' ? '#EF6506' : 'none'}
                    fontSize={'14px'}
                  >
                    Trial
                  </Text>
                  <Text
                    cursor="pointer"
                    onClick={() => handleUsersTab('Free')}
                    borderBottom={
                      selectedTab === 'Free' ? '2.5px solid #EF6506' : 'none'
                    }
                    color={selectedTab === 'Free' ? '#EF6506' : 'none'}
                    fontSize={'14px'}
                  >
                    Free
                  </Text>
                  <Text
                    cursor="pointer"
                    onClick={() => handleUsersTab('inviteUsers')}
                    borderBottom={
                      selectedTab === 'inviteUsers'
                        ? '2.5px solid #EF6506'
                        : 'none'
                    }
                    color={selectedTab === 'inviteUsers' ? '#EF6506' : 'none'}
                    fontSize={'14px'}
                  >
                    Invited
                  </Text>
                </Flex>
              </Flex>
              <Flex alignItems="center" gap="10px" position="relative">
                <Box position="relative" width="200px">
                  <Tooltip
                    label={searchStatus.message}
                    isOpen={searchStatus.showTooltip}
                    placement="bottom"
                    bg="gray.700"
                    color="white"
                    fontSize="12px"
                    px={3}
                    py={2}
                    borderRadius="6px"
                    hasArrow
                    gutter={8}
                  >
                    <Box width="100%">
                      <SearchBar
                        placeholder="Search by name"
                        borderRadius="8px"
                        width="100%"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onFocus={() => {
                          // Track search by name
                          analytics.track('people:searchbyname:clicked', {
                            email: currentUser.email,
                            userRole: currentUser?.role,
                          });
                        }}
                      />
                    </Box>
                  </Tooltip>
                </Box>
                <CustomSelect
                  options={periodOptions}
                  placeholder="6 months"
                  width="150px"
                  value={periods.peoplesPage}
                  onChange={(newPeriod) => {
                    handlePeriodChange('peoplesPage', newPeriod);
                    // Track period change
                    analytics.track('people:periodchange:clicked', {
                      email: currentUser.email,
                      userRole: currentUser?.role,
                      period: newPeriod,
                    });
                  }}
                />
                {/* Delete Button */}
                {isSuperRole && (
                  <>
                    <Box
                      display={'flex'}
                      justifyContent={'center'}
                      alignItems={'center'}
                      border="1px solid #E7E7E7"
                      padding={3}
                      borderRadius="8px"
                      height={'32px'}
                      // width={'32px'}
                      opacity={selectedRows?.length > 0 ? 1 : 0.5}
                      cursor={
                        selectedRows?.length > 0 ? 'pointer' : 'not-allowed'
                      }
                      _disabled={selectedRows?.length > 0 ? false : true}
                      _hover={
                        selectedRows?.length > 0
                          ? {}
                          : {
                              backgroundColor: '#f5f5f5',
                              border: '1px solid #EF6506',
                            }
                      }
                      onClick={() => {
                        selectedRows?.length > 0 && onOpenDelete();
                        // Track delete users
                        analytics.track('people:deleteusers:clicked', {
                          email: currentUser.email,
                          userRole: currentUser?.role,
                        });
                      }}
                    >
                      {<DeleteIcon color={'#EF6506'} />}
                    </Box>
                    <DeleteUserModel
                      isOpen={isOpenDelete}
                      onClose={onCloseDelete}
                      onDeleteUsers={() => handleDeleteUser(selectedRows)}
                    />
                  </>
                )}

                {/* Bulk Assignment Button - visible for admin and super_admin */}
                {(currentUser?.role === 'admin' || isSuperRole) && (
                  <Box
                    display={'flex'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    border="1px solid #E7E7E7"
                    padding={3}
                    borderRadius="8px"
                    height={'32px'}
                    opacity={selectedRows?.length > 0 ? 1 : 0.5}
                    cursor={
                      selectedRows?.length > 0 ? 'pointer' : 'not-allowed'
                    }
                    _disabled={selectedRows?.length > 0 ? false : true}
                    _hover={
                      selectedRows?.length > 0
                        ? {
                            backgroundColor: '#FFF8F3',
                            border: '1px solid #EF6506',
                          }
                        : {}
                    }
                    onClick={() => {
                      selectedRows?.length > 0 &&
                        setIsAssignmentModalOpen(true);
                      // Track assignment button click
                      analytics.track('people:bulkassign:clicked', {
                        email: currentUser.email,
                        userRole: currentUser?.role,
                      });
                    }}
                  >
                    <AddIcon color={'#EF6506'} />
                  </Box>
                )}

                <Box position="relative">
                  <Popover>
                    {({ isOpen, onClose }) => (
                      <>
                        <PopoverTrigger>
                          <Button
                            border={'1px solid #E7E7E7'}
                            borderRadius="8px"
                            bg="none"
                            _hover={{
                              bg: '#FFF8F3',
                              border: '1px solid #EF6506',
                            }}
                            _active={{ bg: '#FFF8F3' }}
                            onClick={() => {
                              // Track customize table
                              analytics.track('people:customizetable:clicked', {
                                email: currentUser.email,
                                userRole: currentUser?.role,
                              });
                            }}
                            height={'32px'}
                            padding={'0'}
                          >
                            <EyeIcon />
                          </Button>
                        </PopoverTrigger>
                        <CustomizeTablePopup
                          isOpen={isOpen}
                          onClose={onClose}
                          visibleColumns={visibleColumns}
                          setVisibleColumns={setVisibleColumns}
                          setSingleUserData={setSingleUserData}
                        />
                      </>
                    )}
                  </Popover>
                </Box>

                {/* Filter Configuration Button */}
                <Box position="relative">
                  <Popover>
                    {({ isOpen, onClose }) => (
                      <>
                        <PopoverTrigger>
                          <Button
                            border="1px solid #E7E7E7"
                            borderRadius="8px"
                            bg="none"
                            _hover={{
                              bg: '#FFF8F3',
                              border: '1px solid #EF6506',
                            }}
                            _active={{ bg: '#FFF8F3' }}
                            onClick={() => {
                              // Track filter config
                              analytics.track('people:filterconfig:clicked', {
                                email: currentUser.email,
                                userRole: currentUser?.role,
                              });
                            }}
                            height="32px"
                            px="3"
                            title="Configure Filters"
                          >
                            <Text
                              fontSize="12px"
                              fontWeight="600"
                              color="#222222"
                            >
                              Filters
                            </Text>
                          </Button>
                        </PopoverTrigger>
                        <FilterConfigPanel
                          isOpen={isOpen}
                          onClose={onClose}
                          visibleFilters={visibleFilters}
                          setVisibleFilters={setVisibleFilters}
                          currentUser={currentUser}
                        />
                      </>
                    )}
                  </Popover>
                </Box>

                {/* <IconButton
                  border="1px solid #E7E7E7"
                  borderRadius="8px"
                  aria-label="Filter"
                  icon={<FilterListFilledIcon />}
                  bg="none"
                  _hover={{ bg: '#FFF8F3' }}
                  _active={{ bg: '#FFF8F3' }}
                /> */}
              </Flex>
            </Flex>
            <CheckTable
              key={JSON.stringify(filteredUsers)}
              columnsData={columnsDataCheck}
              visibleColumns={visibleColumns}
              tableData={mapUserData(filteredUsers)}
              setSingleUserData={setSingleUserData}
              setShowProfile={setShowProfile}
              totalUsersLength={totalUsersLength}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              setItemsPerPage={setItemsPerPage}
              itemsPerPage={itemsPerPage}
              isLoading={isLoading}
              fetchAndFilterUsers={fetchAndFilterUsers}
              isInviteUsers={isInviteUsers}
            />
          </SimpleGrid>
        </>
      ) : (
        <>
          {singleUserData && (
            <PersonalInfo
              singleUserData={singleUserData}
              setShowProfile={setShowProfile}
            />
          )}
        </>
      )}
    </Box>
  );
}
